import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/shadcn/button";
import { ScrollArea } from "@/components/ui/shadcn/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/shadcn/sheet";
import { cn } from "@/lib/utils";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  LucideIcon,
  Menu,
} from "lucide-react";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NavLink, useLocation, useNavigate } from "react-router";

export interface WorkspaceNavItem {
  label: string;
  description?: string;
  path: string;
  Icon: LucideIcon;
  matchPaths?: string[];
  children?: WorkspaceNavChild[];
  /** Small pill next to the label (e.g. "Beta"). */
  badge?: string;
  /** data-tour anchor for the first-run GuidedTour. */
  tourId?: string;
  /** Listed in the phone drawer only; on desktop the same door sits in the top bar. */
  mobileOnly?: boolean;
}

export interface WorkspaceNavChild {
  label: string;
  path: string;
  Icon?: LucideIcon;
}

interface CommandWorkspaceLayoutProps {
  navItems: WorkspaceNavItem[];
  rightPanel?: ReactNode;
  children: ReactNode;
  className?: string;
  title?: string;
  subtitle?: string;
  navFooter?: (collapsed: boolean, onNavigate?: () => void) => ReactNode;
  topActions?: ReactNode;
  /** Optional toolbar actions beside the logo on phones. */
  mobileTopActions?: ReactNode;
  /** Show the existing navigation as an inert preview beneath an account gate. */
  navigationGate?: ReactNode;
  unreadCount?: number;
  /** Rendered above nav links in the sidebar (desktop) and mobile nav sheet */
  navHeaderSlot?: ReactNode;
  upgradeCta?: ReactNode;
}

export const CommandWorkspaceLayout = ({
  navItems,
  rightPanel,
  children,
  className,
  title,
  subtitle,
  navFooter,
  topActions,
  mobileTopActions,
  navigationGate,
  unreadCount,
  navHeaderSlot,
  upgradeCta,
}: CommandWorkspaceLayoutProps) => {
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationTrigger = useRef<HTMLButtonElement | null>(null);

  const computedTitle = useMemo(() => title ?? "Workspace", [title]);

  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Also dismiss for links supplied by the switcher, billing and profile slots.
  useEffect(() => {
    setMobileNavOpen(false);
    setNotificationsOpen(false);
  }, [location.pathname, location.search, location.hash]);

  // Auto-expand parent items when navigating to a child route
  useEffect(() => {
    navItems.forEach((item) => {
      if (
        item.children?.some((child) => location.pathname.startsWith(child.path))
      ) {
        setExpandedItems((prev) => {
          if (prev.has(item.path)) return prev;
          const next = new Set(prev);
          next.add(item.path);
          return next;
        });
      }
    });
  }, [location.pathname, navItems]);

  const renderNavLinks = useCallback(
    (options?: { showDetails?: boolean; onNavigate?: () => void }) => {
      const showDetails = options?.showDetails ?? true;
      const handleNavigate = options?.onNavigate;

      return navItems.map(
        ({
          label,
          description,
          path,
          Icon,
          matchPaths,
          children,
          badge,
          tourId,
          mobileOnly,
        }) => {
          const hasChildren = children && children.length > 0;
          const isExpanded = expandedItems.has(path);

          // Check if any child is active
          const isChildActive = children?.some(
            (child) =>
              location.pathname === child.path ||
              location.pathname.startsWith(child.path + "/"),
          );

          const isActiveFunc = matchPaths
            ? ({ isActive }: { isActive: boolean }) => {
                if (isActive) return true;
                const currentPath = window.location.pathname;
                return matchPaths.some((pattern) => {
                  const regex = new RegExp(
                    pattern.replace(/:\w+/g, "[^/]+").replace(/\//g, "\\/"),
                  );
                  return regex.test(currentPath);
                });
              }
            : undefined;

          // For items with children, clicking toggles expansion
          if (hasChildren) {
            return (
              <div key={path} className="flex flex-col">
                <button
                  type="button"
                  aria-label={!showDetails ? label : undefined}
                  title={!showDetails ? label : undefined}
                  onClick={() => {
                    // Always ensure expanded (don't toggle closed)
                    setExpandedItems((prev) => {
                      if (prev.has(path)) return prev;
                      const next = new Set(prev);
                      next.add(path);
                      return next;
                    });
                    // Navigate to first child route
                    if (children?.[0]) {
                      navigate(children[0].path);
                      handleNavigate?.();
                    }
                  }}
                  className={cn(
                    "workspace-nav-link group relative flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors duration-150 w-full text-left",
                    "text-slate-300 hover:bg-white/5 hover:text-white",
                    isChildActive &&
                      "workspace-nav-active bg-primary/12 text-white ring-1 ring-primary/20 hover:bg-primary/15",
                  )}
                >
                  <Icon
                    className={cn(
                      "h-5 w-5 shrink-0 text-slate-400 transition group-hover:text-primary",
                      isChildActive && "text-primary",
                    )}
                  />
                  {showDetails && (
                    <>
                      <div className="flex min-w-0 flex-1 flex-col break-words">
                        <span>{label}</span>
                        {description && (
                          <span className="text-xs font-normal text-slate-400">
                            {description}
                          </span>
                        )}
                      </div>
                      <ChevronRight
                        className={cn(
                          "h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200",
                          isExpanded && "rotate-90",
                        )}
                      />
                    </>
                  )}
                </button>

                {/* Children */}
                {showDetails && isExpanded && (
                  <div className="ml-4 mt-1 flex flex-col gap-1 border-l border-gray-300/40 pl-3">
                    {children.map((child) => {
                      const childIsActive =
                        location.pathname === child.path ||
                        location.pathname.startsWith(child.path + "/");
                      return (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          onClick={handleNavigate}
                          className={cn(
                            "group relative flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200",
                            "text-gray-700 hover:bg-bg-dark hover:text-primary hover:shadow-[var(--shadow-depth-sm)]",
                            childIsActive &&
                              "bg-bg-light text-primary shadow-[var(--shadow-depth-sm)] ring-1 ring-gray-300/40",
                          )}
                        >
                          {child.Icon && (
                            <child.Icon
                              className={cn(
                                "h-4 w-4 shrink-0 text-gray-500 transition group-hover:text-primary",
                                childIsActive && "text-primary",
                              )}
                            />
                          )}
                          <span className="min-w-0 break-words">
                            {child.label}
                          </span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // Regular nav item without children
          return (
            <NavLink
              key={path}
              to={path}
              end={!matchPaths}
              data-tour={
                tourId ??
                `workspace-nav-${path.split("/").filter(Boolean).at(-1)}`
              }
              aria-label={!showDetails ? label : undefined}
              title={!showDetails ? label : undefined}
              onClick={handleNavigate}
              className={({ isActive }) => {
                const active = isActiveFunc
                  ? isActiveFunc({ isActive })
                  : isActive;
                return cn(
                  "workspace-nav-link group relative flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors duration-150",
                  "text-slate-300 hover:bg-white/5 hover:text-white",
                  mobileOnly && "lg:hidden",
                  active &&
                    "workspace-nav-active bg-primary/12 text-white ring-1 ring-primary/20 hover:bg-primary/15",
                );
              }}
            >
              {({ isActive }) => {
                const active = isActiveFunc
                  ? isActiveFunc({ isActive })
                  : isActive;
                return (
                  <>
                    <Icon
                      className={cn(
                        "h-5 w-5 shrink-0 text-slate-400 transition group-hover:text-primary",
                        active && "text-primary",
                      )}
                    />
                    {showDetails && (
                      <div className="flex min-w-0 flex-col break-words">
                        <span className="flex flex-wrap items-center gap-1.5">
                          {label}
                          {badge && (
                            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-primary">
                              {badge}
                            </span>
                          )}
                        </span>
                        {description && (
                          <span className="text-xs font-normal text-slate-400">
                            {description}
                          </span>
                        )}
                      </div>
                    )}
                  </>
                );
              }}
            </NavLink>
          );
        },
      );
    },
    [navItems, expandedItems, location.pathname, navigate],
  );

  return (
    <div
      className={cn(
        "workspace-shell flex h-[100dvh] min-h-0 overflow-hidden bg-bg-dark",
        className,
      )}
    >
      <a href="#workspace-main" className="workspace-skip">
        Skip to content
      </a>
      {/* Mobile navigation sheet */}
      <Sheet
        open={mobileNavOpen}
        onOpenChange={(open) => {
          setMobileNavOpen(open);
          if (open) setNotificationsOpen(false);
        }}
      >
        <SheetContent
          side="left"
          className="workspace-rail h-[100dvh] min-h-0 w-[85vw] max-w-sm gap-0 overflow-hidden p-0"
        >
          <SheetHeader className="shrink-0 px-6 pb-6 pt-7 text-left ms-2">
            <div className="flex flex-col gap-3">
              <Logo
                variant="A"
                tone="light"
                className="h-5 w-auto max-w-[110px] shrink-0 self-start object-contain"
              />
              <SheetTitle className="text-sm font-bold uppercase tracking-widest text-primary">
                {computedTitle}
              </SheetTitle>
              <SheetDescription className="sr-only">
                {navigationGate
                  ? "Sign in to access your Rentinit workspace."
                  : "Navigate your Rentinit workspace."}
              </SheetDescription>
              {subtitle && (
                <span className="inline-flex w-fit items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold text-primary ring-1 ring-primary/20">
                  {subtitle}
                </span>
              )}
            </div>
          </SheetHeader>
          <div className="workspace-rail-scroll relative min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="flex min-h-full flex-col" inert={!!navigationGate}>
              <div className="px-4 pb-6">
                {topActions && !mobileTopActions && (
                  <div className="mb-4" onClick={() => setMobileNavOpen(false)}>
                    {topActions}
                  </div>
                )}
                {navHeaderSlot && <div className="mb-3">{navHeaderSlot}</div>}
                <nav className="flex flex-col gap-1">
                  {renderNavLinks({
                    showDetails: true,
                    onNavigate: () => setMobileNavOpen(false),
                  })}
                </nav>
              </div>

              <div className="mt-auto shrink-0">
                {upgradeCta && <div className="px-4 pb-3">{upgradeCta}</div>}

                {navFooter && (
                  <div className="border-t border-gray-300/40 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-sm text-gray-700">
                    {navFooter(false, () => setMobileNavOpen(false))}
                  </div>
                )}
              </div>
            </div>
            {navigationGate && (
              <div className="absolute inset-0 z-10 bg-slate-950/25 p-4 pt-2 backdrop-blur-[2px]">
                {navigationGate}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Notifications are an on-demand drawer at every viewport size. */}
      {rightPanel && (
        <Sheet
          open={notificationsOpen}
          onOpenChange={(open) => {
            setNotificationsOpen(open);
            if (open) setMobileNavOpen(false);
          }}
        >
          <SheetContent
            side="right"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              notificationTrigger.current?.focus();
            }}
            className="w-full sm:max-w-md bg-background p-0"
          >
            <SheetHeader className="px-6 py-4 text-left">
              <SheetTitle className="text-base font-semibold text-gray-900">
                Notifications
              </SheetTitle>
              <SheetDescription className="text-sm text-muted-foreground">
                Updates about your properties and account.
              </SheetDescription>
            </SheetHeader>
            <ScrollArea className="min-h-0 flex-1 px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              {rightPanel}
            </ScrollArea>
          </SheetContent>
        </Sheet>
      )}

      <div className="lg:hidden">
        <div className="workspace-mobile-header fixed inset-x-0 top-0 z-40 flex items-center justify-between px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open navigation"
            className="z-50 h-10 w-10 rounded-lg border border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-primary"
            onClick={() => {
              setMobileNavOpen(true);
              setNotificationsOpen(false);
            }}
          >
            <Menu className="h-5 w-5" />
          </Button>

          <Logo
            variant="A"
            tone="light"
            className="h-5 w-auto shrink-0 object-contain"
          />

          {mobileTopActions ? (
            <div className="flex items-center gap-2">{mobileTopActions}</div>
          ) : rightPanel ? (
            <Button
              variant="ghost"
              size="icon"
              data-tour="workspace-notifications"
              aria-label="Open notifications"
              aria-haspopup="dialog"
              aria-expanded={notificationsOpen}
              className="relative z-50 h-10 w-10 rounded-lg border border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-primary"
              onClick={(event) => {
                notificationTrigger.current = event.currentTarget;
                setNotificationsOpen(true);
                setMobileNavOpen(false);
              }}
            >
              <Bell className="h-5 w-5" />
              {unreadCount !== undefined && unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex size-4.5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground shadow-sm">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Button>
          ) : (
            <div className="h-10 w-10" />
          )}
        </div>

        <div className="h-16" />
      </div>

      <aside
        className={cn(
          "workspace-rail relative hidden h-full min-h-0 shrink-0 flex-col transition-[width] duration-200 lg:flex",
          navCollapsed ? "w-[72px]" : "w-[248px]",
        )}
      >
        <div className="relative flex min-h-[132px] shrink-0 items-start justify-between px-4 py-5">
          {!navCollapsed ? (
            <div className="flex flex-col gap-3 ms-4 mt-2">
              <Logo
                variant="A"
                tone="light"
                className="h-5 max-w-[110px] w-auto shrink-0 object-contain"
              />
              <span className="text-sm font-bold uppercase tracking-widest text-primary">
                {computedTitle}
              </span>
              {subtitle && (
                <span className="inline-flex w-fit items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold text-primary ring-1 ring-primary/20">
                  {subtitle}
                </span>
              )}
            </div>
          ) : (
            <Logo variant="It" className="h-6 w-auto shrink-0 object-contain" />
          )}

          <div
            className={cn(
              "absolute -right-3 top-6 z-40",
              navigationGate && "hidden",
            )}
          >
            <Button
              variant="ghost"
              size="icon"
              aria-label={
                navCollapsed ? "Expand navigation" : "Collapse navigation"
              }
              aria-expanded={!navCollapsed}
              className="h-8 w-8 rounded-md border border-white/15 bg-[#142330] text-slate-300 hover:bg-[#1B3040] hover:text-white"
              onClick={() => setNavCollapsed((prev) => !prev)}
            >
              {navCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="workspace-rail-scroll relative min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="flex min-h-full flex-col" inert={!!navigationGate}>
            <div className="px-3 pb-6">
              {navHeaderSlot && !navCollapsed && (
                <div className="mb-3">{navHeaderSlot}</div>
              )}
              <nav className="flex flex-col gap-1">
                {renderNavLinks({ showDetails: !navCollapsed })}
              </nav>
            </div>

            <div className="mt-auto shrink-0">
              {upgradeCta && !navCollapsed && (
                <div className="px-3 pb-3">{upgradeCta}</div>
              )}

              {navFooter && (
                <div className="border-t border-gray-300/40 px-3 py-4 text-sm text-gray-600">
                  {navFooter(navCollapsed)}
                </div>
              )}
            </div>
          </div>
          {navigationGate && (
            <div className="absolute inset-0 z-10 bg-slate-950/25 p-3 pt-2 backdrop-blur-[2px]">
              {navigationGate}
            </div>
          )}
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:flex-row">
        <main
          id="workspace-main"
          tabIndex={-1}
          className="relative flex min-h-0 min-w-0 flex-1 flex-col outline-none"
        >
          <header className="workspace-toolbar hidden lg:flex">
            <div>
              <span>Rentinit</span>
              <span aria-hidden="true">/</span>
              <strong>{computedTitle}</strong>
            </div>
            <div className="flex items-center gap-4">
              {topActions}
              {rightPanel && (
                <Button
                  variant="ghost"
                  size="icon"
                  data-tour="workspace-notifications"
                  aria-label="Open notifications"
                  aria-haspopup="dialog"
                  aria-expanded={notificationsOpen}
                  className="relative size-10"
                  onClick={(event) => {
                    notificationTrigger.current = event.currentTarget;
                    setNotificationsOpen(true);
                  }}
                >
                  <Bell className="size-5" />
                  {!!unreadCount && (
                    <span className="absolute -right-0.5 -top-0.5 flex min-w-4.5 h-4.5 items-center justify-center rounded-full bg-action px-1 text-[10px] font-semibold text-action-foreground">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </header>
          <div className="workspace-content flex-1 overflow-y-auto bg-bg-dark pt-[75px] sm:pt-[72px] lg:pt-0">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
